import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { MAX_WIDTH, resizeImage } from '../lib/images'

export default function ImageManager({ onClose }) {
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(null) // "Resizing…" | "Uploading…" | null
  const [pendingFiles, setPendingFiles] = useState([]) // [{file, caption, credit}]
  const [copied, setCopied] = useState(null) // image id that was just copied
  const [deleting, setDeleting] = useState(null) // image id being deleted
  const [confirmDelete, setConfirmDelete] = useState(null) // image to confirm delete
  const [view, setView] = useState('gallery') // gallery | upload
  const [replacing, setReplacing] = useState(null) // id of the image being replaced

  useEffect(() => { fetchImages() }, [])

  async function fetchImages() {
    setLoading(true)

    // Sync Storage → image_assets (catches files uploaded before manager existed)
    const { data: storageFiles } = await supabase.storage.from('story-images').list('', { limit: 500 })
    const { data: existingAssets } = await supabase.from('image_assets').select('filename')
    const existingFilenames = new Set((existingAssets || []).map(a => a.filename))

    const missing = (storageFiles || []).filter(f =>
      f.name && !f.name.endsWith('/') && !existingFilenames.has(f.name)
    )

    if (missing.length > 0) {
      const inserts = missing.map(f => {
        const { data: { publicUrl } } = supabase.storage.from('story-images').getPublicUrl(f.name)
        return { filename: f.name, public_url: publicUrl, caption: '', credit: '' }
      })
      await supabase.from('image_assets').insert(inserts)
    }

    // Now fetch all assets
    const { data } = await supabase
      .from('image_assets')
      .select('*')
      .order('created_at', { ascending: false })
    setImages(data || [])
    setLoading(false)
  }

  // ── File selection ────────────────────────────────────────
  function handleFileSelect(e) {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    const pending = files.map(file => ({
      file, caption: '', credit: '', id: Math.random().toString(36),
      preview: URL.createObjectURL(file)
    }))
    setPendingFiles(pending)
    setView('upload')
  }

  function updatePending(id, field, value) {
    setPendingFiles(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p))
  }

  function clearPending(items) {
    items.forEach(p => URL.revokeObjectURL(p.preview))
  }

  function removePending(id) {
    setPendingFiles(prev => {
      clearPending(prev.filter(p => p.id === id))
      const next = prev.filter(p => p.id !== id)
      if (next.length === 0) setView('gallery')
      return next
    })
  }

  // ── Upload ────────────────────────────────────────────────
  async function uploadAll() {
    if (!pendingFiles.length) return
    setUploading(true)
    const results = []

    for (const pending of pendingFiles) {
      try {
        setUploadProgress('Resizing ' + pending.file.name + '…')
        const resized = await resizeImage(pending.file)

        setUploadProgress('Uploading ' + pending.file.name + '…')
        const ext = pending.file.name.split('.').pop()
        const filename = Date.now() + '-' + Math.random().toString(36).slice(2) + '.' + ext

        const { error: uploadError } = await supabase.storage
          .from('story-images')
          .upload(filename, resized, { upsert: false })

        if (uploadError) { console.error('Upload error:', uploadError); continue }

        const { data: { publicUrl } } = supabase.storage
          .from('story-images')
          .getPublicUrl(filename)

        const { data: asset } = await supabase.from('image_assets').insert({
          filename,
          public_url: publicUrl,
          caption: pending.caption,
          credit: pending.credit
        }).select().single()

        if (asset) results.push(asset)
      } catch (e) {
        console.error('Error processing', pending.file.name, e)
      }
    }

    setImages(prev => [...results, ...prev])
    clearPending(pendingFiles)
    setPendingFiles([])
    setUploading(false)
    setUploadProgress(null)
    setView('gallery')
  }

  // ── Copy URL ──────────────────────────────────────────────
  async function copyUrl(image) {
    const payload = JSON.stringify({
      url: image.public_url,
      caption: image.caption || '',
      credit: image.credit || ''
    })
    await navigator.clipboard.writeText(payload)
    setCopied(image.id)
    setTimeout(() => setCopied(null), 2000)
  }

  // ── Delete ────────────────────────────────────────────────
  async function deleteImage(image) {
    setDeleting(image.id)
    // Remove from Storage
    const { error: storageError } = await supabase.storage
      .from('story-images')
      .remove([image.filename])
    if (storageError) console.error('Storage delete error:', storageError)
    // Remove from image_assets table
    await supabase.from('image_assets').delete().eq('id', image.id)
    setImages(prev => prev.filter(i => i.id !== image.id))
    setDeleting(null)
    setConfirmDelete(null)
  }

  // ── Replace ───────────────────────────────────────────────
  async function handleReplaceFile(image, file) {
    setReplacing(image.id)
    const resized = await resizeImage(file)
    const ext = file.name.split('.').pop()
    const newFilename = image.id + '-' + Date.now() + '.' + ext
    const oldUrl = image.public_url

    // Upload new file
    const { error } = await supabase.storage
      .from('story-images')
      .upload(newFilename, resized, { upsert: true })
    if (error) { console.error('Replace upload error:', error); setReplacing(null); return }

    const { data: { publicUrl: newUrl } } = supabase.storage
      .from('story-images').getPublicUrl(newFilename)

    // Update image_assets
    await supabase.from('image_assets')
      .update({ filename: newFilename, public_url: newUrl })
      .eq('id', image.id)

    // Propagate to all unpublished stories using old URL
    await supabase.from('stories')
      .update({ image_url: newUrl })
      .eq('image_url', oldUrl)
      .neq('status', 'ready')

    // Delete old file from storage
    await supabase.storage.from('story-images').remove([image.filename])

    setImages(prev => prev.map(i =>
      i.id === image.id ? { ...i, public_url: newUrl, filename: newFilename } : i
    ))
    setReplacing(null)
  }

  // ── Update metadata ───────────────────────────────────────
  async function updateMeta(image, field, value) {
    await supabase.from('image_assets').update({ [field]: value }).eq('id', image.id)
    setImages(prev => prev.map(i => i.id === image.id ? { ...i, [field]: value } : i))
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="imgmgr-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="imgmgr-header">
          <h2 className="imgmgr-title">Image Manager</h2>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            {view === 'gallery' && (
              <label className="btn-primary" style={{ cursor: 'pointer' }}>
                ⬆ Upload
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </label>
            )}
            {view === 'upload' && (
              <button className="btn-secondary" onClick={() => { clearPending(pendingFiles); setPendingFiles([]); setView('gallery') }}>
                ← Back
              </button>
            )}
            <button className="modal-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Upload view */}
        {view === 'upload' && (
          <div className="imgmgr-upload-view">
            <p className="imgmgr-upload-hint">
              Images wider than {MAX_WIDTH}px will be automatically resized. Add caption and credit before uploading.
            </p>
            <div className="imgmgr-pending-list">
              {pendingFiles.map(pending => (
                <div key={pending.id} className="imgmgr-pending-item">
                  <img
                    src={pending.preview}
                    alt=""
                    className="imgmgr-pending-thumb"
                  />
                  <div className="imgmgr-pending-meta">
                    <p className="imgmgr-pending-filename">{pending.file.name}</p>
                    <input
                      className="editor-input"
                      value={pending.caption}
                      onChange={e => updatePending(pending.id, 'caption', e.target.value)}
                      placeholder="Caption"
                    />
                    <input
                      className="editor-input"
                      value={pending.credit}
                      onChange={e => updatePending(pending.id, 'credit', e.target.value)}
                      placeholder="Credit / photographer"
                      style={{ marginTop: '0.4rem' }}
                    />
                  </div>
                  <button className="imgmgr-remove-btn" onClick={() => removePending(pending.id)}>✕</button>
                </div>
              ))}
            </div>
            {uploadProgress && (
              <div className="imgmgr-progress">
                <div className="loading-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                <span>{uploadProgress}</span>
              </div>
            )}
            <div className="imgmgr-upload-actions">
              <button
                className="btn-primary"
                onClick={uploadAll}
                disabled={uploading || pendingFiles.length === 0}
              >
                {uploading ? 'Uploading…' : 'Upload ' + pendingFiles.length + ' image' + (pendingFiles.length > 1 ? 's' : '')}
              </button>
            </div>
          </div>
        )}

        {/* Gallery view */}
        {view === 'gallery' && (
          <div className="imgmgr-gallery">
            {loading && <div className="loading">Loading images…</div>}
            {!loading && images.length === 0 && (
              <div className="imgmgr-empty">No images yet. Click Upload to add some.</div>
            )}
            {!loading && images.map(image => (
              <div key={image.id} className="imgmgr-card">
                <div className="imgmgr-thumb-wrap">
                  <img src={image.public_url} alt={image.caption} className="imgmgr-thumb" />
                </div>
                <div className="imgmgr-card-meta">
                  <EditableField
                    value={image.caption}
                    placeholder="Caption"
                    onSave={val => updateMeta(image, 'caption', val)}
                  />
                  <EditableField
                    value={image.credit}
                    placeholder="Credit"
                    onSave={val => updateMeta(image, 'credit', val)}
                    style={{ color: '#767676', fontSize: '0.9em' }}
                  />
                </div>
                <div className="imgmgr-card-actions">
                  <button
                    className={'imgmgr-copy-btn' + (copied === image.id ? ' copied' : '')}
                    onClick={() => copyUrl(image)}
                    title="Copy URL"
                  >
                    {copied === image.id ? '✓ Copied' : '⎘ Copy'}
                  </button>
                  <label className="imgmgr-replace-btn" title="Replace image"
                    style={{ opacity: replacing === image.id ? 0.5 : 1 }}>
                    {replacing === image.id ? '…' : '↺'}
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      disabled={!!replacing}
                      onChange={e => {
                        const file = e.target.files?.[0]
                        if (file) handleReplaceFile(image, file)
                        e.target.value = ''
                      }} />
                  </label>
                  <button
                    className="imgmgr-delete-btn"
                    onClick={() => setConfirmDelete(image)}
                    disabled={deleting === image.id}
                    title="Delete"
                  >
                    {deleting === image.id ? '…' : '✕'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete confirmation */}
        {confirmDelete && (
          <div className="imgmgr-confirm-overlay" onClick={() => setConfirmDelete(null)}>
            <div className="imgmgr-confirm" onClick={e => e.stopPropagation()}>
              <p className="imgmgr-confirm-text">Delete this image permanently?</p>
              <img src={confirmDelete.public_url} alt="" className="imgmgr-confirm-thumb" />
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
                <button className="btn-primary" style={{ background: '#c0392b' }}
                  onClick={() => deleteImage(confirmDelete)}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Inline editable field ─────────────────────────────────
function EditableField({ value, placeholder, onSave, style }) {
  const [editing, setEditing] = useState(false)
  const [local, setLocal] = useState(value || '')

  useEffect(() => { setLocal(value || '') }, [value])

  function handleBlur() {
    setEditing(false)
    if (local !== value) onSave(local)
  }

  return editing ? (
    <input
      className="imgmgr-inline-input"
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={handleBlur}
      autoFocus
      style={style}
    />
  ) : (
    <p
      className={'imgmgr-meta-text' + (!local ? ' empty' : '')}
      onClick={() => setEditing(true)}
      style={style}
    >
      {local || placeholder}
    </p>
  )
}
